/* Catálogo de personagens/pilotos. Carrega /characters/catalog.json em runtime.
 * Rede só troca o ID; cada cliente monta o visual localmente. */
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { makeProceduralCharacter, disposeVisual } from './CharacterVisual.js'

export const DEFAULT_CHARACTER = 'default'

const BUILTIN = [
    { id: 'default', name: 'Piloto Padrão', owner: 'Carpano', type: 'procedural', style: 'default', color: '#ffcc88' },
    { id: 'cap', name: 'Cap', owner: 'Carpano', type: 'procedural', style: 'default', color: '#ff4444' },
    { id: 'luigi', name: 'Verde', owner: 'Carpano', type: 'procedural', style: 'default', color: '#44aa44' },
    // --- elenco ---
    { id: 'vital', name: 'Vital', owner: 'Vital', type: 'procedural', style: 'bear', color: '#c4783a', scale: 1.05 },
    { id: 'gohan', name: 'Gohan', owner: 'Gohan', type: 'procedural', style: 'mechanic', color: '#e8b090', scale: 0.95 },
    { id: 'artur', name: 'Artur', owner: 'Artur', type: 'procedural', style: 'neonrider', color: '#ffccaa', scale: 1 },
]

class CharacterCatalog {
    constructor() {
        this.chars = new Map()
        BUILTIN.forEach((c) => this.chars.set(c.id, c))
        this.glbCache = new Map()
        this.loaded = false
        this._gltf = null
    }

    async load(url = '/characters/catalog.json') {
        try {
            const res = await fetch(url, { cache: 'no-store' })
            if (res.ok) {
                const data = await res.json()
                for (const c of data.characters || []) {
                    if (c && typeof c.id === 'string' && /^[\w-]{1,32}$/.test(c.id) &&
                        (c.type === 'procedural' || c.type === 'glb')) {
                        this.chars.set(c.id, { name: c.id, owner: '', ...c })
                    }
                }
            }
        } catch (_) { /* só builtins */ }
        this.loaded = true
        return this.list()
    }

    list() { return [...this.chars.values()] }
    has(id) { return this.chars.has(id) }
    get(id) { return this.chars.get(id) || this.chars.get(DEFAULT_CHARACTER) }

    _loader() {
        if (!this._gltf) {
            const draco = new DRACOLoader()
            draco.setDecoderPath('/draco/')
            this._gltf = new GLTFLoader()
            this._gltf.setDRACOLoader(draco)
        }
        return this._gltf
    }

    _loadGlb(path) {
        if (!this.glbCache.has(path)) {
            this.glbCache.set(path, new Promise((resolve, reject) =>
                this._loader().load(path, (g) => resolve(g.scene), undefined, reject)))
        }
        return this.glbCache.get(path)
    }

    /** { group, tint[] } — nunca rejeita */
    async buildVisual(id, fallbackColor = 0xffcc88) {
        const c = this.get(id)
        if (c.type === 'glb') {
            try {
                const scene = await this._loadGlb(c.model)
                return { group: this._fitGlb(scene.clone(true), c), tint: [] }
            } catch (err) {
                console.warn('[characters] falha ao carregar', c.id, err)
            }
        }
        const color = this._parseColor(c.color, fallbackColor)
        return makeProceduralCharacter(color, c.skin, c.scale || 1, c.style || 'default')
    }

    _parseColor(v, fallback) {
        if (typeof v === 'number') return v
        if (typeof v === 'string') {
            const n = parseInt(v.replace('#', ''), 16)
            if (!Number.isNaN(n)) return n
        }
        return fallback
    }

    _fitGlb(model, c) {
        model.traverse((o) => { if (o.isMesh) o.castShadow = true })
        const wrap = new THREE.Group()
        wrap.add(model)
        model.rotation.y = THREE.MathUtils.degToRad(c.rotationY || 0)
        wrap.updateMatrixWorld(true)
        const box = new THREE.Box3().setFromObject(wrap)
        const size = box.getSize(new THREE.Vector3())
        const targetH = 1.15
        const s = (c.scale) || (targetH / Math.max(size.y, 0.001))
        model.scale.setScalar(s)
        wrap.updateMatrixWorld(true)
        box.setFromObject(wrap)
        const off = c.offset || [0, 0, 0]
        const seatY = c.seatY ?? 0.55
        model.position.set(
            -box.getCenter(new THREE.Vector3()).x + off[0],
            seatY - box.min.y + off[1],
            -box.getCenter(new THREE.Vector3()).z + off[2] - 0.15
        )
        return wrap
    }
}

export { disposeVisual }
export default new CharacterCatalog()
